import React, { useState, useEffect, useContext, useRef } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { canUserDelete } from '@/utils/permissions';
import { 
  Truck, Calendar, Search, Filter, RefreshCw, 
  Package, User, FileText, ChevronDown, Plus, X, Save,
  CheckCircle, AlertCircle, Trash2
} from 'lucide-react';

// Searchable Customer Dropdown Component
function CustomerSearchDropdown({ 
  value, 
  selectedCustomer,
  onSelect, 
  customers,
  loading,
  placeholder = "Search and select customer..." 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  
  // Filter customers based on search
  const filteredCustomers = customers.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(term) ||
      c.code?.toLowerCase().includes(term) ||
      c.gstNo?.toLowerCase().includes(term) ||
      c.address?.toLowerCase().includes(term)
    );
  });
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left flex items-center justify-between hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition"
        data-testid="customer-dropdown"
      >
        <span className={selectedCustomer ? 'text-gray-900 font-medium' : 'text-gray-400'}>
          {selectedCustomer ? `${selectedCustomer.code} - ${selectedCustomer.name}` : placeholder}
        </span>
        <ChevronDown size={16} className={`text-gray-400 transition ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <Search size={16} className="text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by code, name, GST..."
                className="flex-1 bg-transparent text-sm outline-none"
                autoFocus
              />
            </div>
          </div>
          
          {/* Customer List */}
          <div className="max-h-60 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-3 text-center text-gray-500 text-sm">Loading customers...</div>
            ) : filteredCustomers.length === 0 ? (
              <div className="px-4 py-3 text-center text-gray-500 text-sm">No customers found</div>
            ) : (
              filteredCustomers.map((customer, idx) => (
                <div
                  key={customer.id || idx}
                  onClick={() => {
                    onSelect(customer);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`px-4 py-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${
                    selectedCustomer?.code === customer.code ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded mr-2">
                        {customer.code}
                      </span>
                      <span className="text-sm font-medium text-gray-900">{customer.name}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      customer.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {customer.status || 'Active'}
                    </span>
                  </div>
                  {customer.address && (
                    <p className="text-xs text-gray-500 mt-1 truncate">{customer.address}</p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DispatchRecords() {
  const { API, user } = useContext(AppContext);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProduct, setFilterProduct] = useState('all');
  const [filterCustomer, setFilterCustomer] = useState('all');
  const [filterFY, setFilterFY] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  
  // Customer master data
  const [masterCustomers, setMasterCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);
  const [editFormData, setEditFormData] = useState({
    rate: '',
    amount: '',
    gstPercent: '',
    totalValue: '',
    quantity: '',
    vehicleNo: '',
    driverName: '',
    status: ''
  });
  const [updating, setUpdating] = useState(false);
  
  // Check if user has delete permission (Admin or 'full' access)
  const canDelete = canUserDelete(user, '/dispatch/records');
  
  // Add record modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    invoiceNo: '',
    date: new Date().toISOString().split('T')[0],
    customerCode: '',
    customerName: '',
    productType: 'CUBES',
    drossType: '', // Sub-type for DROSS
    quantity: '',
    vehicleNo: '',
    driverName: '',
    rate: '',
    amount: '',
    gstPercent: '18',
    totalValue: '',
    status: 'Dispatched'
  });

  // Product types - expanded with scrap types
  const productTypes = [
    // Finished Goods
    'CUBES', 'NOTCH BAR', 'SHOTS', 'INGOTS',
    // Dross (will show sub-type text box)
    'DROSS',
    // Scrap Types
    'ZINC SCRAP', 'COPPER SCRAP', 'BRASS SCRAP', 'STEEL SCRAP', 'IRON SCRAP'
  ];
  
  // Status options
  const statusOptions = ['Dispatched', 'Pending', 'In Transit', 'Delivered', 'Rejected'];

  // Financial Year months (April to March)
  const fyMonths = [
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
  ];

  // Generate financial years (from 2020-21 to current+1)
  const generateFYOptions = () => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    // If before April, we're still in previous FY
    const currentFY = currentMonth >= 4 ? currentYear : currentYear - 1;
    
    const options = [];
    for (let year = 2020; year <= currentFY + 1; year++) {
      options.push({
        value: `${year}-${(year + 1).toString().slice(-2)}`,
        label: `FY ${year}-${(year + 1).toString().slice(-2)}`,
        startYear: year,
        endYear: year + 1
      });
    }
    return options.reverse(); // Most recent first
  };

  const fyOptions = generateFYOptions();

  // Reset form data
  const resetForm = () => {
    setFormData({
      invoiceNo: '',
      date: new Date().toISOString().split('T')[0],
      customerCode: '',
      customerName: '',
      productType: 'CUBES',
      drossType: '',
      quantity: '',
      vehicleNo: '',
      driverName: '',
      rate: '',
      amount: '',
      gstPercent: '18',
      totalValue: '',
      status: 'Dispatched'
    });
    setSelectedCustomer(null);
  };

  // Load master customers
  const loadMasterCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const response = await axios.get(`${API}/master-data/dispatch/customers`);
      const activeCustomers = (response.data.data || []).filter(c => c.status !== 'Inactive');
      setMasterCustomers(activeCustomers);
    } catch (error) {
      console.error('Error loading master customers:', error);
    }
    setLoadingCustomers(false);
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setFormData(prev => ({
      ...prev,
      customerCode: customer.code,
      customerName: customer.name
    }));
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.invoiceNo.trim()) {
      setMessage({ type: 'error', text: 'Invoice number is required' });
      return;
    }
    if (!formData.customerCode || !formData.customerName) {
      setMessage({ type: 'error', text: 'Please select a customer from the list' });
      return;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setMessage({ type: 'error', text: 'Valid quantity is required' });
      return;
    }
    if (formData.productType === 'DROSS' && !formData.drossType) {
      setMessage({ type: 'error', text: 'Please select a Dross Type' });
      return;
    }

    setSaving(true);
    try {
      // Format date to DD-MM-YY for Google Sheets
      const dateObj = new Date(formData.date);
      const formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}-${(dateObj.getMonth() + 1).toString().padStart(2, '0')}-${dateObj.getFullYear().toString().slice(-2)}`;
      
      // Determine display product type (include dross type if applicable)
      let displayProductType = formData.productType;
      if (formData.productType === 'DROSS' && formData.drossType) {
        displayProductType = formData.drossType; // Use specific dross type
      }
      
      const payload = {
        invoiceNo: formData.invoiceNo.trim(),
        date: formattedDate,
        customerCode: formData.customerCode.trim(),
        customerName: formData.customerName.trim(),
        productType: displayProductType,
        productCategory: formData.productType, // Original category for stock deduction
        drossType: formData.productType === 'DROSS' ? formData.drossType : '',
        quantity: parseFloat(formData.quantity),
        vehicleNo: formData.vehicleNo.trim() || 'N/A',
        driverName: formData.driverName.trim() || 'N/A',
        rate: formData.rate ? parseFloat(formData.rate) : 0,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
        gstPercent: formData.gstPercent ? parseFloat(formData.gstPercent) : 0,
        totalValue: formData.totalValue ? parseFloat(formData.totalValue) : 0,
        status: formData.status
      };

      await axios.post(`${API}/sync/dispatch`, payload);
      
      setMessage({ type: 'success', text: 'Dispatch record added successfully! It will sync to Google Sheets.' });
      resetForm();
      setShowAddModal(false);
      
      // Refresh records
      fetchRecords();
    } catch (error) {
      console.error('Error adding dispatch record:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to add dispatch record' 
      });
    } finally {
      setSaving(false);
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  // Handle delete dispatch record
  const handleDelete = async () => {
    if (!recordToDelete) return;
    
    setDeleting(true);
    try {
      // Try to delete by invoiceNo first (for Google Sheets synced records)
      const invoiceNo = recordToDelete.invoiceNo || recordToDelete.id;
      await axios.delete(`${API}/sheets/sync/dispatch/${encodeURIComponent(invoiceNo)}`);
      
      setMessage({ type: 'success', text: 'Dispatch record deleted successfully!' });
      setShowDeleteModal(false);
      setRecordToDelete(null);
      
      // Refresh records
      fetchRecords();
    } catch (error) {
      console.error('Error deleting dispatch record:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to delete dispatch record' 
      });
    } finally {
      setDeleting(false);
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  // Open edit modal with record data
  const openEditModal = (record) => {
    setRecordToEdit(record);
    const baseAmount = record.amount || record.amountWithoutGst || record.value || '';
    const gst = record.gstPercent || 18;
    const total = record.totalValue || record.amountWithGst || '';
    setEditFormData({
      rate: record.rate || '',
      amount: baseAmount,
      gstPercent: gst,
      totalValue: total,
      quantity: record.quantity || '',
      vehicleNo: record.vehicleNo || '',
      driverName: record.driverName || '',
      status: record.status || 'Dispatched'
    });
    setShowEditModal(true);
  };

  // Handle edit form input change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Auto-calculate amount when rate changes
      if (name === 'rate' && prev.quantity) {
        const rate = parseFloat(value) || 0;
        const qty = parseFloat(prev.quantity) || 0;
        if (rate > 0 && qty > 0) {
          const baseAmount = rate * qty * 1000;
          updated.amount = baseAmount.toFixed(2);
          // Also recalculate total value with GST
          const gst = parseFloat(prev.gstPercent) || 0;
          updated.totalValue = (baseAmount + (baseAmount * gst / 100)).toFixed(2);
        }
      }
      
      // Auto-calculate total value when amount or GST changes
      if (name === 'amount' || name === 'gstPercent') {
        const baseAmount = name === 'amount' ? (parseFloat(value) || 0) : (parseFloat(prev.amount) || 0);
        const gst = name === 'gstPercent' ? (parseFloat(value) || 0) : (parseFloat(prev.gstPercent) || 0);
        updated.totalValue = (baseAmount + (baseAmount * gst / 100)).toFixed(2);
      }
      
      return updated;
    });
  };

  // Handle update dispatch record
  const handleUpdate = async () => {
    if (!recordToEdit) return;
    
    setUpdating(true);
    try {
      const invoiceNo = recordToEdit.invoiceNo || recordToEdit.id;
      
      await axios.put(`${API}/dispatch/records/${encodeURIComponent(invoiceNo)}`, {
        rate: editFormData.rate ? parseFloat(editFormData.rate) : null,
        amount: editFormData.amount ? parseFloat(editFormData.amount) : null,
        gstPercent: editFormData.gstPercent ? parseFloat(editFormData.gstPercent) : null,
        totalValue: editFormData.totalValue ? parseFloat(editFormData.totalValue) : null,
        quantity: editFormData.quantity ? parseFloat(editFormData.quantity) : null,
        vehicleNo: editFormData.vehicleNo || null,
        driverName: editFormData.driverName || null,
        status: editFormData.status || null
      });
      
      setMessage({ type: 'success', text: 'Dispatch record updated successfully!' });
      setShowEditModal(false);
      setRecordToEdit(null);
      
      // Refresh records
      fetchRecords();
    } catch (error) {
      console.error('Error updating dispatch record:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to update dispatch record' 
      });
    } finally {
      setUpdating(false);
    }
    
    setTimeout(() => setMessage(null), 5000);
  };

  useEffect(() => {
    fetchRecords();
    loadMasterCustomers();
  }, []);

  // Create a map of customer codes to customer data for quick lookup
  const customerCodeMap = React.useMemo(() => {
    const map = new Map();
    masterCustomers.forEach(c => {
      if (c.code) {
        map.set(c.code.toUpperCase(), c);
      }
    });
    return map;
  }, [masterCustomers]);

  // Get customer name from code using master data
  const getCustomerNameFromCode = (code) => {
    if (!code) return '-';
    const customer = customerCodeMap.get(code.toUpperCase());
    return customer?.name || '-'; // Return '-' if name not found in master data
  };

  // Check if a value looks like a customer code (short alphanumeric, typically 2-10 chars)
  const looksLikeCustomerCode = (value) => {
    if (!value) return false;
    // Customer codes are typically short (2-10 chars), all uppercase or alphanumeric
    const trimmed = value.trim();
    return trimmed.length <= 15 && /^[A-Z0-9\-_]+$/i.test(trimmed);
  };

  // Get customer code from record (handles legacy data where code might be in customerName)
  const getCustomerCode = (record) => {
    // If customerCode exists, use it
    if (record.customerCode) return record.customerCode;
    // If customerName looks like a code (short alphanumeric), treat it as code
    if (record.customerName && looksLikeCustomerCode(record.customerName)) {
      return record.customerName;
    }
    return '-';
  };

  // Get customer name from record (handles legacy data)
  const getCustomerName = (record) => {
    // First, determine the customer code
    const code = record.customerCode || 
                 (record.customerName && looksLikeCustomerCode(record.customerName) ? record.customerName : null);
    
    // If we have a code, look up the name from master data
    if (code) {
      const name = getCustomerNameFromCode(code);
      if (name !== '-') return name;
    }
    
    // If customerName doesn't look like a code, return it as the name
    if (record.customerName && !looksLikeCustomerCode(record.customerName)) {
      return record.customerName;
    }
    
    return '-';
  };

  // Map productType → source module label + color
  const getSourceLabel = (record) => {
    const pt = (record.productType || record.productCategory || '').toString().toUpperCase();
    if (!pt) return null;
    if (['CUBES', 'NOTCH BAR', 'NOTCHBAR', 'SHOTS', 'INGOTS'].some(x => pt.includes(x))) {
      return { label: 'Finished Goods', classes: 'bg-blue-50 text-blue-700 border-blue-200' };
    }
    if (pt.includes('DROSS') || record.drossType) {
      return { label: 'Dross Stock', classes: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    if (pt.includes('ZINC') || pt.includes('COPPER') || pt.includes('BRASS')) {
      return { label: 'Reds & Heavies', classes: 'bg-pink-50 text-pink-700 border-pink-200' };
    }
    if (pt.includes('STEEL') || pt.includes('IRON')) {
      return { label: 'Ferrous Scrap', classes: 'bg-slate-100 text-slate-700 border-slate-300' };
    }
    if (pt.includes('WOOD') && pt.includes('PELLET')) {
      return { label: 'Consumable Stock', classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    return null;
  };

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sync/dispatch`);
      const data = response.data.data || [];
      console.log('Fetched records:', data.length);
      console.log('Sample record:', data[0]);
      setRecords(data);
    } catch (error) {
      console.error('Error fetching dispatch records:', error);
    }
    setLoading(false);
  };

  // Parse date from various formats (DD-MM-YY, YYYY-MM-DD, etc.)
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return null;
    
    let day, month, year;
    
    // Format: DD-MM-YY (e.g., 28-02-26)
    if (/^\d{1,2}-\d{1,2}-\d{2}$/.test(dateStr)) {
      const parts = dateStr.split('-');
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parseInt(parts[2]) + 2000; // Convert 26 to 2026
    }
    // Format: YYYY-MM-DD (e.g., 2026-02-28)
    else if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateStr)) {
      const parts = dateStr.split('-');
      year = parseInt(parts[0]);
      month = parseInt(parts[1]);
      day = parseInt(parts[2]);
    }
    // Format: DD/MM/YYYY
    else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('/');
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parseInt(parts[2]);
    }
    else {
      return null;
    }
    
    return { day, month, year };
  };

  // Get financial year for a date
  const getFYForDate = (dateStr) => {
    const parsed = parseDate(dateStr);
    if (!parsed) return null;
    
    const { month, year } = parsed;
    // If month is Jan-Mar, it belongs to previous year's FY
    const fyStartYear = month >= 4 ? year : year - 1;
    return `${fyStartYear}-${(fyStartYear + 1).toString().slice(-2)}`;
  };

  // Get month string (01-12) from date
  const getMonthFromDate = (dateStr) => {
    const parsed = parseDate(dateStr);
    if (!parsed) return null;
    return parsed.month.toString().padStart(2, '0');
  };

  // Get unique values for filters - case-insensitive deduplication
  const products = React.useMemo(() => {
    const productMap = new Map();
    records.forEach(r => {
      if (r.productType) {
        // Use uppercase key for deduplication, but store the original value
        const key = r.productType.toUpperCase().trim();
        if (!productMap.has(key)) {
          productMap.set(key, r.productType.toUpperCase().trim());
        }
      }
    });
    const uniqueProducts = [...productMap.values()];
    console.log('Products for filter:', uniqueProducts);
    return uniqueProducts;
  }, [records]);
  
  const customers = React.useMemo(() => {
    const customerMap = new Map();
    records.forEach(r => {
      if (r.customerName) {
        const key = r.customerName.toUpperCase().trim();
        if (!customerMap.has(key)) {
          customerMap.set(key, r.customerName.trim());
        }
      }
    });
    const uniqueCustomers = [...customerMap.values()];
    console.log('Customers for filter:', uniqueCustomers);
    return uniqueCustomers;
  }, [records]);

  // Filter records
  const filteredRecords = records.filter(r => {
    // Search filter (case-insensitive)
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      (r.invoiceNo || '').toLowerCase().includes(searchLower) ||
      (r.customerName || '').toLowerCase().includes(searchLower) ||
      (r.productType || '').toLowerCase().includes(searchLower) ||
      (r.vehicleNo || '').toLowerCase().includes(searchLower);
    
    // Product filter (case-insensitive comparison)
    const matchesProduct = filterProduct === 'all' || 
      (r.productType || '').toLowerCase() === filterProduct.toLowerCase();
    
    // Customer filter (case-insensitive comparison)
    const matchesCustomer = filterCustomer === 'all' || 
      (r.customerName || '').toLowerCase() === filterCustomer.toLowerCase();
    
    // Financial Year filter
    let matchesFY = true;
    if (filterFY !== 'all') {
      const recordFY = getFYForDate(r.date);
      matchesFY = recordFY === filterFY;
    }
    
    // Month filter
    let matchesMonth = true;
    if (filterMonth !== 'all') {
      const recordMonth = getMonthFromDate(r.date);
      matchesMonth = recordMonth === filterMonth;
    }
    
    return matchesSearch && matchesProduct && matchesCustomer && matchesFY && matchesMonth;
  });

  // Sort records chronologically by date (1st to 31st)
  const sortedRecords = [...filteredRecords].sort((a, b) => {
    // Sort by date (invoice date), newest first
    const dateA = parseDate(a.date);
    const dateB = parseDate(b.date);
    if (!dateA || !dateB) return 0;
    
    // Sort descending (newest first)
    if (dateA.year !== dateB.year) return dateB.year - dateA.year;
    if (dateA.month !== dateB.month) return dateB.month - dateA.month;
    return dateB.day - dateA.day;
  });

  // Calculate totals (excluding rejected)
  const totalQty = sortedRecords
    .filter(r => r.status !== 'Rejected')
    .reduce((sum, r) => sum + (parseFloat(r.quantity) || 0), 0);
  const totalDispatches = sortedRecords.filter(r => r.status !== 'Rejected').length;
  const rejectedCount = sortedRecords.filter(r => r.status === 'Rejected').length;

  // Format date for display
  const formatDate = (dateStr) => {
    const parsed = parseDate(dateStr);
    if (!parsed) return dateStr || '-';
    
    const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${parsed.day} ${monthNames[parsed.month]} ${parsed.year}`;
  };

  // Get filter summary text
  const getFilterSummary = () => {
    let summary = '';
    if (filterFY !== 'all') {
      summary += `FY ${filterFY}`;
      if (filterMonth !== 'all') {
        const monthLabel = fyMonths.find(m => m.value === filterMonth)?.label || '';
        summary += ` - ${monthLabel}`;
      }
    } else {
      summary = 'All Years';
    }
    return summary;
  };

  return (
    <div className="space-y-6" data-testid="dispatch-records-page">
      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-xl flex items-center gap-3 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <Truck className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Dispatch Records</h1>
            <p className="text-sm text-gray-500">
              {getFilterSummary()} • Bi-directional sync with Google Sheets
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition shadow-sm"
            data-testid="add-dispatch-btn"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add Dispatch</span>
            <span className="sm:hidden">Add</span>
          </button>
          <button
            onClick={fetchRecords}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Add Dispatch Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <Truck className="text-white" size={24} />
                <h2 className="text-lg font-bold text-white">Add Dispatch Record</h2>
              </div>
              <button
                onClick={() => { setShowAddModal(false); resetForm(); }}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Row 1: Invoice No & Date */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="invoiceNo"
                    value={formData.invoiceNo}
                    onChange={handleInputChange}
                    placeholder="e.g., INV-001"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
              </div>

              {/* Row 2: Customer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer <span className="text-red-500">*</span>
                </label>
                <CustomerSearchDropdown
                  value={formData.customerCode}
                  selectedCustomer={selectedCustomer}
                  onSelect={handleCustomerSelect}
                  customers={masterCustomers}
                  loading={loadingCustomers}
                  placeholder="Search and select customer..."
                />
                {selectedCustomer && (
                  <p className="text-xs text-gray-500 mt-1">
                    <span className="font-medium">GST:</span> {selectedCustomer.gstNo || 'N/A'} | 
                    <span className="font-medium ml-2">Address:</span> {selectedCustomer.address?.substring(0, 50) || 'N/A'}...
                  </p>
                )}
              </div>

              {/* Row 3: Product Type & Quantity */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Product Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={(e) => {
                      handleInputChange(e);
                      // Reset dross type when product type changes
                      if (e.target.value !== 'DROSS') {
                        setFormData(prev => ({ ...prev, drossType: '' }));
                      }
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    <optgroup label="Finished Goods">
                      <option value="CUBES">CUBES</option>
                      <option value="NOTCH BAR">NOTCH BAR</option>
                      <option value="SHOTS">SHOTS</option>
                      <option value="INGOTS">INGOTS</option>
                    </optgroup>
                    <optgroup label="Dross">
                      <option value="DROSS">DROSS</option>
                    </optgroup>
                    <optgroup label="Scrap Materials">
                      <option value="ZINC SCRAP">ZINC SCRAP</option>
                      <option value="COPPER SCRAP">COPPER SCRAP</option>
                      <option value="BRASS SCRAP">BRASS SCRAP</option>
                      <option value="STEEL SCRAP">STEEL SCRAP</option>
                      <option value="IRON SCRAP">IRON SCRAP</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="WOOD PELLETS">WOOD PELLETS</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity (MT) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    placeholder="e.g., 25.5"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    required
                  />
                </div>
              </div>

              {/* Conditional: Dross Type (only when DROSS is selected) */}
              {formData.productType === 'DROSS' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <label className="block text-sm font-medium text-amber-800 mb-2">
                    Dross Type <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="drossType"
                    value={formData.drossType}
                    onChange={handleInputChange}
                    placeholder="e.g., DROSS (50%), WHITE DROSS, AL POWDER..."
                    className="w-full px-4 py-2.5 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none bg-white"
                    required
                  />
                  <p className="text-xs text-amber-600 mt-1">
                    Enter the specific dross type. This will be shown in dispatch records and deducted from Dross Stock.
                  </p>
                </div>
              )}

              {/* Info note for scrap types */}
              {['ZINC SCRAP', 'COPPER SCRAP', 'BRASS SCRAP'].includes(formData.productType) && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-700">
                    <strong>{formData.productType}</strong> will be deducted from <strong>Reds & Heavies</strong> stock
                  </p>
                </div>
              )}
              {['STEEL SCRAP', 'IRON SCRAP'].includes(formData.productType) && (
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3">
                  <p className="text-xs text-gray-700">
                    <strong>{formData.productType}</strong> will be deducted from <strong>Ferrous Scrap</strong> stock
                  </p>
                </div>
              )}

              {/* Row 4: Vehicle No & Driver Name */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle No
                  </label>
                  <input
                    type="text"
                    name="vehicleNo"
                    value={formData.vehicleNo}
                    onChange={handleInputChange}
                    placeholder="e.g., MH12AB1234"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Name
                  </label>
                  <input
                    type="text"
                    name="driverName"
                    value={formData.driverName}
                    onChange={handleInputChange}
                    placeholder="Enter driver name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Row 5: Rate & Base Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rate (₹/KG)
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={(e) => {
                      handleInputChange(e);
                      // Auto-calculate amount if quantity exists
                      const rate = parseFloat(e.target.value) || 0;
                      const qty = parseFloat(formData.quantity) || 0;
                      if (rate > 0 && qty > 0) {
                        const baseAmount = rate * qty * 1000;
                        const gst = parseFloat(formData.gstPercent) || 0;
                        const total = baseAmount + (baseAmount * gst / 100);
                        setFormData(prev => ({ 
                          ...prev, 
                          amount: baseAmount.toFixed(2),
                          totalValue: total.toFixed(2)
                        }));
                      }
                    }}
                    placeholder="e.g., 250"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Amount (₹) <span className="text-gray-400 text-xs">(Before GST)</span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={formData.amount}
                    onChange={(e) => {
                      handleInputChange(e);
                      // Recalculate total value
                      const baseAmount = parseFloat(e.target.value) || 0;
                      const gst = parseFloat(formData.gstPercent) || 0;
                      const total = baseAmount + (baseAmount * gst / 100);
                      setFormData(prev => ({ ...prev, totalValue: total.toFixed(2) }));
                    }}
                    placeholder="Amount before GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">Auto-calculated: Rate (₹/KG) × Qty (MT) × 1000</p>
                </div>
              </div>

              {/* Row 6: GST % & Total Value */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    GST %
                  </label>
                  <input
                    type="number"
                    name="gstPercent"
                    value={formData.gstPercent}
                    onChange={(e) => {
                      handleInputChange(e);
                      // Recalculate total value
                      const baseAmount = parseFloat(formData.amount) || 0;
                      const gst = parseFloat(e.target.value) || 0;
                      const total = baseAmount + (baseAmount * gst / 100);
                      setFormData(prev => ({ ...prev, totalValue: total.toFixed(2) }));
                    }}
                    placeholder="e.g., 18"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-700 mb-1">
                    Total Value (₹) <span className="text-green-600 text-xs font-normal">(With GST - Added to Revenue)</span>
                  </label>
                  <input
                    type="number"
                    name="totalValue"
                    value={formData.totalValue}
                    onChange={handleInputChange}
                    placeholder="Total with GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border-2 border-green-400 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none bg-green-50 font-semibold text-green-700"
                  />
                  <p className="text-[10px] text-green-600 mt-1">This value will be added to Dashboard Revenue</p>
                </div>
              </div>

              {/* Row 7: Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              {/* Info Note */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <p>
                  <strong>Note:</strong> This record will be automatically synced to the connected Google Sheet.
                </p>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Add Dispatch
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && recordToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-red-500 to-red-600 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <Trash2 className="text-white" size={24} />
                <h2 className="text-lg font-bold text-white">Delete Dispatch Record</h2>
              </div>
              <button
                onClick={() => { setShowDeleteModal(false); setRecordToDelete(null); }}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this dispatch record?
              </p>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p><strong>Invoice:</strong> {recordToDelete.invoiceNo}</p>
                <p><strong>Customer:</strong> {recordToDelete.customerName}</p>
                <p><strong>Product:</strong> {recordToDelete.productType}</p>
                <p><strong>Quantity:</strong> {recordToDelete.quantity} MT</p>
              </div>
              <p className="text-red-600 text-sm mt-4">
                This action cannot be undone. The record will also be removed from the connected Google Sheet.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => { setShowDeleteModal(false); setRecordToDelete(null); }}
                className="px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-6 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Delete Record
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Dispatch Modal */}
      {showEditModal && recordToEdit && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-dispatch-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-blue-600 rounded-t-2xl">
              <div className="flex items-center gap-3">
                <FileText className="text-white" size={24} />
                <h2 className="text-lg font-bold text-white">Edit Dispatch Record</h2>
              </div>
              <button
                onClick={() => { setShowEditModal(false); setRecordToEdit(null); }}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Record Info (Read-only) */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice:</span>
                  <span className="font-semibold text-gray-900">{recordToEdit.invoiceNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="font-medium text-gray-700">{recordToEdit.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Customer:</span>
                  <span className="font-medium text-gray-700">{recordToEdit.customerName || recordToEdit.customerCode}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Product:</span>
                  <span className="font-medium text-gray-700">{recordToEdit.productType}</span>
                </div>
              </div>

              {/* Editable Fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Quantity (MT)</label>
                    <input
                      type="number"
                      name="quantity"
                      value={editFormData.quantity}
                      onChange={handleEditInputChange}
                      placeholder="e.g., 25.5"
                      step="any"
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate (₹/MT)</label>
                    <input
                      type="number"
                      name="rate"
                      value={editFormData.rate}
                      onChange={handleEditInputChange}
                      placeholder="e.g., 250"
                      step="any"
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Amount (₹) <span className="text-gray-400 text-xs">(Before GST)</span>
                  </label>
                  <input
                    type="number"
                    name="amount"
                    value={editFormData.amount}
                    onChange={handleEditInputChange}
                    placeholder="Amount before GST"
                    step="any"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    data-testid="edit-amount-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST %</label>
                    <input
                      type="number"
                      name="gstPercent"
                      value={editFormData.gstPercent}
                      onChange={handleEditInputChange}
                      placeholder="e.g., 18"
                      step="any"
                      min="0"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-green-700 mb-1">
                      Total Value (₹) <span className="text-green-600 text-xs font-normal">(With GST)</span>
                    </label>
                    <input
                      type="number"
                      name="totalValue"
                      value={editFormData.totalValue}
                      onChange={handleEditInputChange}
                      placeholder="Total with GST"
                      step="any"
                      min="0"
                      className="w-full px-4 py-2.5 border-2 border-green-400 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none bg-green-50 font-semibold text-green-700"
                      data-testid="edit-total-value-input"
                    />
                    <p className="text-[10px] text-green-600 mt-1">This value goes to Dashboard Revenue</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle No</label>
                    <input
                      type="text"
                      name="vehicleNo"
                      value={editFormData.vehicleNo}
                      onChange={handleEditInputChange}
                      placeholder="e.g., MH12AB1234"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Driver Name</label>
                    <input
                      type="text"
                      name="driverName"
                      value={editFormData.driverName}
                      onChange={handleEditInputChange}
                      placeholder="Enter driver name"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    name="status"
                    value={editFormData.status}
                    onChange={handleEditInputChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => { setShowEditModal(false); setRecordToEdit(null); }}
                className="px-4 py-2.5 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition font-medium disabled:opacity-50 flex items-center gap-2"
                data-testid="save-edit-btn"
              >
                {updating ? (
                  <>
                    <RefreshCw size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Year & Month Tabs */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={18} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Financial Year:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => { setFilterFY('all'); setFilterMonth('all'); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filterFY === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="fy-all-btn"
            >
              All Years
            </button>
            {fyOptions.map(fy => (
              <button
                key={fy.value}
                onClick={() => { setFilterFY(fy.value); setFilterMonth('all'); }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  filterFY === fy.value 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`fy-${fy.value}-btn`}
              >
                {fy.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Month selector - only show when a specific FY is selected */}
        {filterFY !== 'all' && (
          <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Month:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterMonth('all')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  filterMonth === 'all' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="month-all-btn"
              >
                All Months
              </button>
              {fyMonths.map(month => (
                <button
                  key={month.value}
                  onClick={() => setFilterMonth(month.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    filterMonth === month.value 
                      ? 'bg-green-600 text-white' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`month-${month.value}-btn`}
                >
                  {month.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Dispatches</p>
              <p className="text-2xl font-bold text-gray-900">{totalDispatches}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="text-green-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Quantity</p>
              <p className="text-2xl font-bold text-gray-900">{totalQty.toFixed(0)} MT</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <User className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Customers</p>
              <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Truck className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Products</p>
              <p className="text-2xl font-bold text-gray-900">{products.length > 0 ? products.join(', ') : '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm space-y-4">
        {/* Search */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search invoice, customer, vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
              data-testid="search-input"
            />
          </div>
        </div>
        
        {/* Product Filter Buttons */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Filter by Product</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterProduct('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filterProduct === 'all' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="product-all-btn"
            >
              All Products
            </button>
            {products.map(p => (
              <button
                key={p}
                onClick={() => setFilterProduct(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  filterProduct === p 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`product-${p}-btn`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        
        {/* Customer Filter Buttons */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-2">Filter by Customer</label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterCustomer('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filterCustomer === 'all' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="customer-all-btn"
            >
              All Customers
            </button>
            {customers.map(c => (
              <button
                key={c}
                onClick={() => setFilterCustomer(c)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                  filterCustomer === c 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`customer-${c}-btn`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Invoice No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Product</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Qty (MT)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    Loading...
                  </td>
                </tr>
              ) : sortedRecords.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                    No dispatch records found for selected period
                  </td>
                </tr>
              ) : (
                sortedRecords.map((record, idx) => (
                  <tr key={record.dispatchId || idx} className={`hover:bg-gray-50 transition ${record.status === 'Rejected' ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${record.status === 'Rejected' ? 'text-red-600 line-through' : 'text-gray-900'}`}>{record.invoiceNo || '-'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(record.date)}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">{getCustomerCode(record)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-800">{getCustomerName(record)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        record.productType === 'Cubes' || record.productType === 'CUBES' ? 'bg-blue-100 text-blue-700' :
                        record.productType === 'Notch Bar' || record.productType === 'NOTCH BAR' ? 'bg-purple-100 text-purple-700' :
                        record.productType === 'Shots' || record.productType === 'SHOTS' ? 'bg-green-100 text-green-700' :
                        record.productType === 'Ingots' || record.productType === 'INGOTS' ? 'bg-orange-100 text-orange-700' :
                        record.productType?.includes('DROSS') || record.drossType ? 'bg-amber-100 text-amber-700' :
                        record.productType?.includes('ZINC') || record.productType?.includes('COPPER') || record.productType?.includes('BRASS') ? 'bg-pink-100 text-pink-700' :
                        record.productType?.includes('STEEL') || record.productType?.includes('IRON') ? 'bg-slate-200 text-slate-700' :
                        record.productType?.includes('WOOD') ? 'bg-emerald-100 text-emerald-800' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {record.productType || '-'}
                      </span>
                      {record.drossType && (
                        <p className="text-xs text-amber-600 mt-0.5">{record.drossType}</p>
                      )}
                      {(() => {
                        const source = getSourceLabel(record);
                        if (!source) return null;
                        return (
                          <span
                            className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 text-[10px] font-semibold rounded border ${source.classes}`}
                            title={`Deducted from ${source.label}`}
                            data-testid={`dispatch-source-badge-${idx}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70"></span>
                            {source.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`font-semibold ${record.status === 'Rejected' ? 'text-red-600' : 'text-gray-900'}`}>
                        {record.quantity || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{record.vehicleNo || '-'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        record.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {record.status || 'Dispatched'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => openEditModal(record)}
                          className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition"
                          title="Edit record"
                          data-testid={`edit-dispatch-${record.invoiceNo}`}
                        >
                          <FileText size={15} />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => { setRecordToDelete(record); setShowDeleteModal(true); }}
                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                            title="Delete record"
                            data-testid={`delete-dispatch-${record.invoiceNo}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {sortedRecords.length > 0 && (
              <tfoot className="bg-gray-50 border-t border-gray-200">
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-right font-semibold text-gray-700">
                    Total ({getFilterSummary()}){rejectedCount > 0 ? ` - ${rejectedCount} rejected excluded` : ''}:
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">{totalQty.toFixed(0)} MT</td>
                  <td colSpan={4}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">Financial Year Note</p>
        <p className="text-blue-600">
          Financial Year runs from <strong>April 1</strong> to <strong>March 31</strong>. 
          For example, FY 2025-26 covers April 2025 to March 2026.
        </p>
      </div>
    </div>
  );
}

export default DispatchRecords;
